// src/pages/chat/Chat.tsx

import { useRef, useState, useEffect } from "react";
import { Checkbox, Panel, DefaultButton, TextField, SpinButton } from "@fluentui/react";
import { useUser } from "../../contexts/useUserData";
import elypseImg from "../../assets/img/MIA_circulo-azul.png";
import { fetchImageUrl } from "../../api/api";

import styles from "./Chat.module.css";

import { chatApi, Approaches, AskResponse, ChatRequest, ChatTurn, saveLastScope } from "../../api";
import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { useLayout } from "../../contexts/useLayoutContext";
import { NewChat } from "../../components/LayoutComponents/NewChat";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { LikeButton } from "../../components/LikeDislike/Buttons/LikeButton";
import { DislikeButton } from "../../components/LikeDislike/Buttons/DislikeButton";
import { useChat } from "../../contexts/useChatData";
import { ButtonFav } from "../../components/Favorites/ButtonFav";

const Chat = () => {
    const {
        answers,
        answersCleaned,
        cleanAnswers,
        putAnswer,
        removeChatData,
        loading,
        isLoading,
        linkToken,
        inputDisabled,
        putInputDisabled
    } = useChat();
    const { token, clickedFavQuestion, isFavQuestionClicked, scope, seccion } = useUser();

    const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(false);
    const [promptTemplate, setPromptTemplate] = useState<string>("");
    const [retrieveCount, setRetrieveCount] = useState<number>(3);
    const [useSemanticRanker, setUseSemanticRanker] = useState<boolean>(true);
    const [useSemanticCaptions, setUseSemanticCaptions] = useState<boolean>(true);
    const [excludeCategory, setExcludeCategory] = useState<string>("");
    const [useSuggestFollowupQuestions, setUseSuggestFollowupQuestions] = useState<boolean>(true);
    const [imageUrls, setImageUrls] = useState<string[]>([]);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const [error, setError] = useState<unknown>();

    const [activeCitation, setActiveCitation] = useState<string>();
    const [activeAnalysisPanelTab, setActiveAnalysisPanelTab] = useState<AnalysisPanelTabs | undefined>(undefined);

    const [selectedAnswer, setSelectedAnswer] = useState<number>(0);

    // Unified feedback status: 'none' | 'liked' | 'disliked'
    const [feedbackStatus, setFeedbackStatus] = useState<{ [key: number]: 'none' | 'liked' | 'disliked' }>({});

    const [indexOpenedPopup, setIndexOpenedPopup] = useState<number | null>(null);

    const [selectedButton, setSelectedButton] = useState<string>("Vigente"); // Definimos por defecto que esté en mdo "Vigente"

    const { menuOpen, toogleMenuOpen } = useLayout();

    const makeApiRequest = async (question: string) => {
        lastQuestionRef.current = question;

        error && setError(undefined);
        loading!(true);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);

        try {
            const history: ChatTurn[] = answers!.map(a => ({ user: a[0], assistant: a[1].answer }));
            let final_token = "";
            if (token) {
                final_token = token;
            }
            const request: ChatRequest = {
                history: [...history, { user: question, assistant: undefined }],
                approach: Approaches.ReadRetrieveRead,
                token: final_token,
                scope: scope!,
                overrides: {
                    promptTemplate: promptTemplate.length === 0 ? undefined : promptTemplate,
                    excludeCategory: excludeCategory.length === 0 ? undefined : excludeCategory,
                    top: retrieveCount,
                    semanticRanker: useSemanticRanker,
                    semanticCaptions: useSemanticCaptions,
                    suggestFollowupQuestions: useSuggestFollowupQuestions
                },
                selectedButton: selectedButton,
                seccion: seccion
            };
            console.log("Request final:", request);
            const result = await chatApi(request);
            putAnswer!(question, result);
        } catch (e) {
            setError(e);
        } finally {
            loading!(false);
        }
    };

    const clearChat = () => {
        lastQuestionRef.current = "";
        error && setError(undefined);
        setActiveCitation(undefined);
        setActiveAnalysisPanelTab(undefined);
        setFeedbackStatus({});
    };

    useEffect(() => {
        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [isLoading]);

    useEffect(() => {
        const result = saveLastScope(scope!, seccion!);
    }, [scope]);

    useEffect(() => {
        if (answers!.length !== Object.keys(feedbackStatus).length) {
            const newStatus = { ...feedbackStatus };
            answers!.forEach((_, index) => {
                if (feedbackStatus[index] === undefined) {
                    newStatus[index] = 'none';
                }
            });
            setFeedbackStatus(newStatus);
        }
    }, [answers]);

    useEffect(() => {
        if (answersCleaned) {
            removeChatData!(clearChat);
            cleanAnswers!(false);
        }
    }, [answersCleaned]);

    useEffect(() => {
        if (clickedFavQuestion![0]) {
            makeApiRequest(clickedFavQuestion![1]);
            isFavQuestionClicked!(false, "");
        }
    }, [clickedFavQuestion]);

    useEffect(() => {
        const newChatElement = document.getElementById("new-chat");

        if (newChatElement) {
            if (isLoading) {
                newChatElement.setAttribute("disabled", "true");
                newChatElement.classList.add("disabled-opacity");
            } else {
                newChatElement.removeAttribute("disabled");
                newChatElement.classList.remove("disabled-opacity");
            }
        }
    }, [isLoading]);

    const toggleMenu = () => {
        toogleMenuOpen!();
        if (!menuOpen) {
            document.body.classList.add("open-nav");
        } else {
            document.body.classList.remove("open-nav");
        }
    };

    const setFeedback = (type: 'like' | 'dislike', index: number): void => {
        setFeedbackStatus(prevStatus => ({
            ...prevStatus,
            [index]: type === 'like' ? 'liked' : 'disliked',
        }));
    };

    function closePopup(index: number) {
        if (indexOpenedPopup === index) return false;
        else return true;
    }

    // Add the missing function definitions
    const onShowCitation = (citation: string, index: number) => {
        document.body.classList.add("modal-open");
        if (activeCitation === citation && activeAnalysisPanelTab === AnalysisPanelTabs.CitationTab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveCitation(citation);
            setActiveAnalysisPanelTab(AnalysisPanelTabs.CitationTab);
        }

        setSelectedAnswer(index);
    };

    const onToggleTab = (tab: AnalysisPanelTabs, index: number) => {
        if (activeAnalysisPanelTab === tab && selectedAnswer === index) {
            setActiveAnalysisPanelTab(undefined);
        } else {
            setActiveAnalysisPanelTab(tab);
        }

        setSelectedAnswer(index);
    };

    const onPromptTemplateChange = (_ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setPromptTemplate(newValue || "");
    };

    const onRetrieveCountChange = (_ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => {
        setRetrieveCount(parseInt(newValue || "3"));
    };

    const onUseSemanticRankerChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticRanker(!!checked);
    };

    const onUseSemanticCaptionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSemanticCaptions(!!checked);
    };

    const onExcludeCategoryChanged = (_ev?: React.FormEvent, newValue?: string) => {
        setExcludeCategory(newValue || "");
    };

    const onUseSuggestFollowupQuestionsChange = (_ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        setUseSuggestFollowupQuestions(!!checked);
    };

    const handleButtonClick = (button: string) => {
        setSelectedButton(button);
        console.log(`Selected button: ${button}`);
    };    

    useEffect(() => {
        const getImage = async () => {
            const data = await fetchImageUrl(seccion!);
            setImageUrls(data.urls);
        };
        getImage();
    }, []);

    return (
        <div className={styles.container}>
            <div className={styles.chatRoot}>
                <div className={styles.chatContainer}>
                    {!lastQuestionRef.current ? (
                        <div className={styles.chatEmptyState}>
                            {imageUrls.length > 0 && (
                                <div style={{ textAlign: "center" }}>
                                    {imageUrls.map((url, idx) => (
                                        <img key={idx} src={url} alt={`Viñeta explicativa ${idx + 1}`} style={{ maxWidth: "25%", margin: "0 8px" }} />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream}>
                            {answers!.map((answer, index) => (
                                <div key={index}>
                                    <div className={styles.questionModule}>
                                        <div className={styles.questionPosition}>
                                            <UserChatMessage message={answer[0]} />
                                            <div className={styles.wrapperButtonFav}>
                                                <ButtonFav
                                                    index={index}
                                                    question={answer[0]}
                                                    signalPopup={setIndexOpenedPopup}
                                                    closePopup={closePopup(index)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className={styles.chatMessageGpt}>
                                        <div className={styles.chatMessageResponse}>
                                            <img src={elypseImg} alt="logo-elypse" />

                                            <Answer
                                                key={index}
                                                answer={answer[1]}
                                                isSelected={selectedAnswer === index && activeAnalysisPanelTab !== undefined}
                                                onCitationClicked={c => onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onFollowupQuestionClicked={q => makeApiRequest(q)}
                                                showFollowupQuestions={useSuggestFollowupQuestions && answers!.length - 1 === index}
                                                linkToken={linkToken!}
                                                seccion={seccion}
                                            />
                                            <div className={styles.wrapperButtonsLikeDislike}>
                                                <LikeButton
                                                    key={index}
                                                    index={index}
                                                    answer={answer}
                                                    feedbackStatus={feedbackStatus[index]}
                                                    setFeedback={setFeedback}
                                                />
                                                <DislikeButton
                                                    key={index}
                                                    index={index}
                                                    answer={answer}
                                                    feedbackStatus={feedbackStatus[index]}
                                                    setFeedback={setFeedback}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {isLoading && (
                                <>
                                    <div className={styles.questionPosition}>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                    </div>
                                    <div className={styles.chatMessageGpt}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}
                            {error ? (
                                <>
                                    <div className={styles.questionPosition}>
                                        <UserChatMessage message={lastQuestionRef.current} />
                                    </div>
                                    <div className={styles.chatMessageGpt}>
                                        <AnswerError error={error.toString()} onRetry={() => makeApiRequest(lastQuestionRef.current)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={chatMessageStreamEnd} />
                        </div>
                    )}

                    <div className={styles.buttonContainer}>
                        <div className={styles.tooltipvigencia}>
                            <button
                                className={`${styles.button} ${selectedButton === "Vigente" ? styles.selectedButton : ""} ${selectedButton === "Vigente" ? styles.buttonVigente : ""}`}
                                onClick={() => handleButtonClick("Vigente")}
                            >
                                Vigente
                            </button>
                            <span className={styles.tooltipvigenciatext}>
                                Responder utilizando únicamente información <strong>vigente</strong>.
                            </span>
                        </div>
                        <div className={styles.tooltipnovigencia}>
                            <button
                                className={`${styles.button} ${selectedButton === "No vigente" ? styles.selectedButton : ""} ${selectedButton === "No vigente" ? styles.buttonNoVigente : ""}`}
                                onClick={() => handleButtonClick("No vigente")}
                            >
                                No vigente
                            </button>
                            <span className={styles.tooltipnovigenciatext}>
                                Responder utilizando únicamente promociones o productos <strong>no vigentes</strong>.
                            </span>
                        </div>
                        <div className={styles.tooltipvigencia}>
                            <button
                                className={`${styles.button} ${selectedButton === "Ambos" ? styles.selectedButton : ""} ${selectedButton === "Ambos" ? styles.buttonVigente : ""}`}
                                onClick={() => handleButtonClick("Ambos")}
                            >
                                Ambos
                            </button>
                            <span className={styles.tooltipvigenciatext}>
                                Responder con información vigente o no vigente de manera <strong>automática</strong>.
                            </span>
                        </div>
                    </div>
                    <div className={styles.chatInput}>
                        <NewChat id="new-chat" menuOpen={toggleMenu} openChat={menuOpen!} setImageUrls={setImageUrls} seccion={seccion!} />
                        <div style={{ flex: 1 }}>
                            <QuestionInput
                                clearOnSend
                                placeholder={inputDisabled! ? "Tienes que dar Feedback para continuar" : "Escribe una pregunta"}
                                disabled={isLoading! ? isLoading : inputDisabled!}
                                onSend={question => makeApiRequest(question)}
                            />
                        </div>
                    </div>
                </div>

                {answers!.length > 0 && activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={activeCitation}
                        onActiveTabChanged={x => onToggleTab(x, selectedAnswer)}
                        citationHeight="810px"
                        answer={answers![selectedAnswer][1]}
                        activeTab={activeAnalysisPanelTab}
                    />
                )}

                <Panel
                    headerText="Configure answer generation"
                    isOpen={isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel="Close"
                    onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>Close</DefaultButton>}
                    isFooterAtBottom={true}
                >
                    <TextField
                        className={styles.chatSettingsSeparator}
                        defaultValue={promptTemplate}
                        label="Override prompt template"
                        multiline
                        autoAdjustHeight
                        onChange={onPromptTemplateChange}
                    />

                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label="Retrieve this many documents from search:"
                        min={1}
                        max={50}
                        defaultValue={retrieveCount.toString()}
                        onChange={onRetrieveCountChange}
                    />
                    <TextField
                        className={styles.chatSettingsSeparator}
                        label="Exclude category"
                        onChange={onExcludeCategoryChanged}
                    />
                    <Checkbox
                        className={styles.chatSettingsSeparator}
                        checked={useSemanticRanker}
                        label="Use semantic ranker for retrieval"
                        onChange={onUseSemanticRankerChange}
                    />
                    <Checkbox
                        className={styles.chatSettingsSeparator}
                        checked={useSemanticCaptions}
                        label="Use query-contextual summaries instead of whole documents"
                        onChange={onUseSemanticCaptionsChange}
                        disabled={!useSemanticRanker}
                    />
                    <Checkbox
                        className={styles.chatSettingsSeparator}
                        checked={useSuggestFollowupQuestions}
                        label="Suggest follow-up questions"
                        onChange={onUseSuggestFollowupQuestionsChange}
                    />
                </Panel>
            </div>
        </div>
    );
};

export default Chat;
